import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Phone, ArrowRight, AlertCircle, Ticket, Sparkles } from 'lucide-react';
import { register } from '../api/api';

const RegisterScreen = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobileno: '',
        password: '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const data = await register(formData);
            if (data.success === 1) {
                navigate('/login');
            } else {
                setError(data.message || 'Registration failed.');
            }
        } catch (err) {
            setError('An error occurred during registration.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6 font-outfit relative overflow-hidden">
             {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/5 rounded-full -translate-x-1/3 translate-y-1/3 blur-3xl"></div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md bg-white rounded-[3rem] shadow-2xl shadow-primary/10 border border-primary/10 p-10 relative z-10"
            >
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20">
                        <Ticket className="text-white" size={30} />
                    </div>
                    <span className="text-primary font-bold uppercase tracking-[0.3em] text-[10px]">Join the Journey</span>
                    <h2 className="text-3xl font-playfair font-black text-secondary mt-2">Create Account</h2>
                    <div className="h-1 w-10 bg-primary mt-3 rounded-full mx-auto"></div>
                </div>

                <form onSubmit={handleRegister} className="space-y-4">
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-red-50 text-red-600 p-4 rounded-2xl flex items-center gap-3 text-sm font-bold border border-red-100"
                        >
                            <AlertCircle size={18} />
                            {error}
                        </motion.div>
                    )}

                    <div className="space-y-3">
                        <div>
                            <label className="block text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1.5 ml-2">Full Name</label>
                            <div className="relative">
                                <User className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={18} />
                                <input
                                    name="name"
                                    type="text"
                                    required
                                    placeholder="John Doe"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-6 py-3.5 bg-cream/50 border border-primary/10 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-semibold text-secondary text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1.5 ml-2">Email Address</label>
                            <div className="relative">
                                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={18} />
                                <input
                                    name="email"
                                    type="email"
                                    required
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-6 py-3.5 bg-cream/50 border border-primary/10 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-semibold text-secondary text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1.5 ml-2">Phone Number</label>
                            <div className="relative">
                                <Phone className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={18} />
                                <input
                                    name="mobileno"
                                    type="tel"
                                    required
                                    placeholder="+91 98765 43210"
                                    value={formData.mobileno}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-6 py-3.5 bg-cream/50 border border-primary/10 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-semibold text-secondary text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-secondary/40 uppercase tracking-widest mb-1.5 ml-2">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={18} />
                                <input
                                    name="password"
                                    type="password"
                                    required
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full pl-12 pr-6 py-3.5 bg-cream/50 border border-primary/10 rounded-2xl focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all font-semibold text-secondary text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-secondary hover:bg-primary text-white py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all shadow-xl shadow-secondary/10 disabled:opacity-70 group"
                        >
                            {loading ? (
                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <span>Begin Journey</span>
                                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </div>

                    <div className="text-center pt-4">
                        <p className="text-secondary/50 font-bold text-sm">
                            Already have an account?{' '}
                            <Link to="/login" className="text-primary hover:text-secondary font-black transition-colors">
                                Sign In
                            </Link>
                        </p>
                    </div>
                </form>
            </motion.div>
        </div>
    );
};

export default RegisterScreen;
